import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import {
  PROFILE_LISTINGS_CATEGORY_CORALS,
  PROFILE_LISTINGS_SORT_NEWEST,
  PROFILE_LISTINGS_SORT_OLDEST,
  PROFILE_LISTINGS_SORT_PRICE_HIGH,
  PROFILE_LISTINGS_SORT_PRICE_LOW,
} from '../../util/profileListingsQuery';

import { IconArrowHead, Menu, MenuContent, MenuItem, MenuLabel } from '../../components';

import css from './ProfileListingsToolbar.module.css';

const TOP_LABEL_IDS = {
  corals: 'ProfilePage.listingsFilterCorals',
  fish: 'ProfilePage.listingsFilterFish',
  dry_goods: 'ProfilePage.listingsFilterDryGoods',
};

const SORT_OPTIONS = [
  { key: PROFILE_LISTINGS_SORT_NEWEST, labelId: 'SortBy.newest' },
  { key: PROFILE_LISTINGS_SORT_OLDEST, labelId: 'SortBy.oldest' },
  { key: PROFILE_LISTINGS_SORT_PRICE_LOW, labelId: 'SortBy.lowestPrice' },
  { key: PROFILE_LISTINGS_SORT_PRICE_HIGH, labelId: 'SortBy.highestPrice' },
];

const FilterMenu = props => {
  const [isOpen, setIsOpen] = useState(false);
  const { id, label, options, selectedKey, onSelect } = props;

  const selectOption = optionKey => {
    setIsOpen(false);
    onSelect(optionKey);
  };

  return (
    <Menu
      useArrow={false}
      contentPlacementOffset={0}
      contentPosition="left"
      onToggleActive={setIsOpen}
      isOpen={isOpen}
      preferScreenWidthOnMobile
    >
      <MenuLabel rootClassName={css.menuLabel} id={id}>
        {label}
        <IconArrowHead
          className={classNames(css.iconArrow, { [css.iconArrowAnimation]: isOpen })}
          direction="down"
          size="tiny"
        />
      </MenuLabel>
      <MenuContent className={css.menuContent}>
        {options.map(option => {
          const selected = selectedKey === option.key;
          return (
            <MenuItem key={option.key}>
              <button
                className={css.menuItem}
                onClick={() => (selected ? null : selectOption(option.key))}
              >
                <span className={selected ? css.menuItemBorderSelected : css.menuItemBorder} />
                {option.label}
              </button>
            </MenuItem>
          );
        })}
      </MenuContent>
    </Menu>
  );
};

/**
 * Category, coral-type, and sort dropdowns for the profile listings tab.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.visibility - { showTopDropdown, showCoralDropdown, showSort, topOptions, coralOptions }
 * @param {Object} props.current - { category, subcategory, sort }
 * @param {Array<{id: string, name: string}>} [props.coralChildren]
 * @param {Function} props.onChange
 * @param {Object} [props.intl]
 * @returns {JSX.Element}
 */
const ProfileListingsToolbar = props => {
  const intlFromHook = useIntl();
  const { visibility = {}, current = {}, coralChildren = [], onChange, intl: intlProp } = props;
  const intl = intlProp || intlFromHook;

  const {
    showTopDropdown = false,
    showCoralDropdown = false,
    showSort = false,
    topOptions = [],
    coralOptions = [],
  } = visibility;
  const { category = null, subcategory = null, sort = null } = current;

  const format = id => intl.formatMessage({ id });

  const topMenuOptions = [
    { key: 'all', label: format('ProfilePage.listingsFilterAll') },
    ...topOptions.map(option => ({
      key: option.key,
      label: format(TOP_LABEL_IDS[option.key] || option.key),
    })),
  ];
  const selectedTopKey = category || 'all';
  const selectedTopLabel =
    topMenuOptions.find(option => option.key === selectedTopKey)?.label ||
    format('ProfilePage.listingsFilterAll');

  const coralById = new Map(coralChildren.map(child => [child.id, child]));
  const coralMenuOptions = [
    { key: 'all', label: format('ProfilePage.listingsFilterCoralTypeAll') },
    ...coralOptions
      .map(option => {
        const child = coralById.get(option.key);
        return child ? { key: child.id, label: child.name } : null;
      })
      .filter(Boolean),
  ];
  const selectedCoralKey = subcategory || 'all';
  const selectedCoralLabel =
    coralMenuOptions.find(option => option.key === selectedCoralKey)?.label ||
    format('ProfilePage.listingsFilterCoralTypeAll');

  const sortMenuOptions = SORT_OPTIONS.map(option => ({
    key: option.key,
    label: format(option.labelId),
  }));
  const selectedSortKey = sort || PROFILE_LISTINGS_SORT_NEWEST;
  const selectedSortLabel =
    sortMenuOptions.find(option => option.key === selectedSortKey)?.label ||
    format('SortBy.newest');

  const handleTopSelect = key => {
    onChange({
      category: key === 'all' ? null : key,
      subcategory: null,
      page: 1,
    });
  };

  const handleCoralSelect = key => {
    if (key === 'all') {
      onChange({
        category: showTopDropdown ? PROFILE_LISTINGS_CATEGORY_CORALS : null,
        subcategory: null,
        page: 1,
      });
      return;
    }
    onChange({
      ...(showTopDropdown ? { category: PROFILE_LISTINGS_CATEGORY_CORALS } : {}),
      subcategory: key,
      page: 1,
    });
  };

  const handleSortSelect = key => {
    onChange({
      sort: key === PROFILE_LISTINGS_SORT_NEWEST ? null : key,
      page: 1,
    });
  };

  return (
    <nav className={css.root} aria-label={format('ProfilePage.screenreader.listingsToolbar')}>
      {showTopDropdown ? (
        <FilterMenu
          id="ProfileListingsToolbar.category"
          label={selectedTopLabel}
          options={topMenuOptions}
          selectedKey={selectedTopKey}
          onSelect={handleTopSelect}
        />
      ) : null}
      {showCoralDropdown ? (
        <FilterMenu
          id="ProfileListingsToolbar.coralType"
          label={selectedCoralLabel}
          options={coralMenuOptions}
          selectedKey={selectedCoralKey}
          onSelect={handleCoralSelect}
        />
      ) : null}
      {showSort ? (
        <FilterMenu
          id="ProfileListingsToolbar.sort"
          label={selectedSortLabel}
          options={sortMenuOptions}
          selectedKey={selectedSortKey}
          onSelect={handleSortSelect}
        />
      ) : null}
    </nav>
  );
};

export default ProfileListingsToolbar;
